import { css, html } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';

import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { predefinedColorsList } from './common/predefined-colors';
import './te-icon';
import { getRGB, rgbToHsv, hsvToRgbDevided, rgbToHex, toHexColor } from './common/converters';

const DEFAULT_COLOR = '#FF0000';

/**
 * Color Picker Component
 *
 * @slot - Default slot for custom color picker control
 */
@customElement('te-color-picker')
export class TeColorPicker extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-color-picker {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: fit-content;
        background-color: ${theme.colors.shade0};
        border-radius: 4px;
        box-shadow: ${theme.shadows.secondary};
        padding: 16px;
      }

      .normal {
        width: 160px;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .te-color-picker &:after {
        display: table;
        content: '';
        clear: both;
      }

      .color-list {
        display: flex;
        flex-direction: column;
        width: 184px;
        height: 80px;
        flex-wrap: wrap;
      }

      .color-list-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-left: 16px;
      }

      .color-list-hybrid {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        height: 192px;
      }

      .color-list-hybrid-new {
        position: absolute;
        top: 94%;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .color-list-hybrid-color,
      .color-list-color {
        width: 16px;
        height: 16px;
        border-radius: 2px;
        margin: 2px;
        cursor: pointer;
      }

      .color-list-hybrid-color:focus-visible,
      .color-list-color:focus-visible {
        outline: none;
      }

      div {
        float: left;
        position: relative;
      }

      &:not(:first-child) {
        margin-left: 10px;
      }

      .color-palette,
      .color-slider {
        cursor: default;
        user-select: none;
      }

      canvas {
        display: block;
        user-select: none;
        border-radius: 4px;
      }

      .color-slider canvas {
        border-radius: 20px;
        position: relative;
        padding: 4px;
      }

      .color-palette .selector {
        position: absolute;
        top: 8px;
        left: 154px;
        height: 16px;
        width: 16px;
        border-radius: 100%;
        border: 2px solid ${theme.colors.shade0};
        transform: translate(-50%, -50%);
      }

      .color-slider .selector {
        position: absolute;
        height: 16px;
        width: 16px;
        top: 8px;
        border-radius: 100%;
        border: 2px solid ${theme.colors.shade0};
        transform: translate(-50%, -50%);
      }

      te-icon {
        margin: 2px;
        cursor: pointer;
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ];

  /** Currently selected color picker color */
  @property() selectedColor = DEFAULT_COLOR;
  /** Pre-styled color picker mode */
  @property() variant: 'default' | 'predefined' | 'hybrid' = 'default';

  // state variables
  @state() colorPalette?: HTMLCanvasElement;

  @state() colorPaletteCtx?: CanvasRenderingContext2D;

  @state() colorSlider?: HTMLCanvasElement;

  @state() colorSliderCtx?: CanvasRenderingContext2D;

  @state() selectedColorShade = DEFAULT_COLOR;

  @state() newColors: string[] = [];

  @state() isColorModalShow = false;

  @state() selectedColorIndex?: number;
  @state() isNewColorSelected = false;

  @state() colorCoords: {
    x: number;
    y: number;
  } = { x: 160, y: 0 };

  @state() imageData?: ImageData;

  @state() colorSliderClicked = false;
  @state() colorPalletterClicked = false;
  @state() isColorPickerOpen = true;
  @state() isSelectorMoved = false;
  @state() startColor = DEFAULT_COLOR;
  @state() isCustom = false;
  @state() count = 0;

  // query selectors
  @query('#slider-selector') sliderSelector?: HTMLElement;
  @query('#canvas-selector') paletterSelector?: HTMLElement;
  @query('slot') customSlot?: HTMLSlotElement;

  @state() onDocumentListenFn: (e: Event) => void;
  @state() onDocumentMouseUpFn: (e: MouseEvent) => void;
  @state() onDocumentMouseMoveFn: (e: MouseEvent) => void;

  constructor() {
    super();
    this.onDocumentListenFn = this.onDocumentListen.bind(this);
    this.onDocumentMouseUpFn = this.onMouseUp.bind(this);
    this.onDocumentMouseMoveFn = this.onMouseMove.bind(this);

    document.addEventListener('click', this.onDocumentListenFn);
    document.addEventListener('mousemove', this.onDocumentMouseMoveFn);
    document.addEventListener('mouseup', this.onDocumentMouseUpFn);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentListenFn);
    document.removeEventListener('mousemove', this.onDocumentMouseMoveFn);
    document.removeEventListener('mouseup', this.onDocumentMouseUpFn);
  }

  onDocumentListen(e: Event) {
    const showCase = (e.target as Element)?.shadowRoot?.querySelector('te-color-picker');
    if (
      showCase?.nodeName === 'TE-COLOR-PICKER' ||
      (e.target as Element)?.nodeName === 'TE-COLOR-PICKER'
    ) {
      return;
    }
    this.isColorPickerOpen = false;
  }

  updated(changedProps: Map<string, unknown>) {
    const { isNewColorSelected, selectedColorIndex, startColor } = this;

    if (isNewColorSelected && selectedColorIndex !== undefined) {
      this.newColors[selectedColorIndex] = startColor;
    }
    this.startColor = toHexColor(
      changedProps.has('selectedColor') ? this.selectedColor : this.startColor
    );
    this.sendCustomEvent('teChange', this.isColorPickerOpen);
  }

  firstUpdated() {
    this.startColor = toHexColor(this.selectedColor);
    this.createColorPicker();
    if (this.customSlot) {
      const custom = this.customSlot.assignedNodes({ flatten: true }).length > 0;
      this.isCustom = custom;
      // Color picker won't be shown initially when using custom element
      this.isColorPickerOpen = !custom;
    }
  }

  createColorPicker() {
    const { startColor } = this;

    if (this.variant === 'predefined') return;

    const { red, green, blue } = getRGB(startColor);

    const { h, s, v } = rgbToHsv(red, green, blue);

    const selectedColor = this.hsvToRgb(h, 1, 1);

    this.colorPalette = this.shadowRoot?.querySelector(
      '.color-palette>canvas'
    ) as HTMLCanvasElement;

    const paletterSelectorX = s * this.colorPalette.width;
    const paletterSelectorY = (1 - v) * this.colorPalette.height;
    const { paletterSelector } = this;

    if (this.colorPalette) {
      this.colorPaletteCtx = this.colorPalette.getContext('2d') as CanvasRenderingContext2D;

      this.createColorPalette(h);
      if (paletterSelector) {
        paletterSelector.style.top = `${paletterSelectorY}px`;
        paletterSelector.style.left = `${paletterSelectorX}px`;
      }
    }

    this.colorCoords = {
      x: paletterSelectorX,
      y: paletterSelectorY,
    };

    this.colorSlider = this.shadowRoot?.querySelector('.color-slider>canvas') as HTMLCanvasElement;

    if (this.colorSlider) {
      this.colorSliderCtx = this.colorSlider.getContext('2d') as CanvasRenderingContext2D;

      this.createColorSlider();
    }

    if (this.sliderSelector) {
      this.sliderSelector.style.background = selectedColor;
      this.sliderSelector.style.left = `${h * this.colorSlider.width}px`;
    }

    const selector = this.shadowRoot?.querySelector<HTMLElement>('.selector');
    if (selector) {
      selector.style.background = startColor;
    }
  }

  getMouseCoords(e: MouseEvent, canvas: HTMLCanvasElement) {
    const position = canvas.getBoundingClientRect();
    let x = e.x - position.left;
    let y = e.y - position.top;
    if (x < 0) x = 0;
    if (x >= canvas.width) x = canvas.width - 1;
    if (y < 0) y = 0;
    if (y >= canvas.height) y = canvas.height - 1;

    const mouseCoords = { x, y };
    if (canvas === this.colorPalette) {
      this.colorCoords = mouseCoords;
    }

    return mouseCoords;
  }

  selectColor() {
    const ctx = this.colorPaletteCtx;
    const { x, y } = this.colorCoords;

    if (!ctx) return;
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    this.startColor = `rgb(${r},${g},${b})`;

    const selector = this.shadowRoot?.querySelector<HTMLElement>('.selector');
    if (selector) {
      selector.style.background = this.startColor;
      selector.style.top = `${y}px`;
      selector.style.left = `${x}px`;
    }
  }

  createColorPalette(h: number) {
    const { colorPalette, colorPaletteCtx } = this;
    if (!colorPalette || !colorPaletteCtx) return;
    if (!this.imageData)
      this.imageData = colorPaletteCtx.createImageData(colorPalette.width, colorPalette.height);
    let i = 0;

    for (let y = 0; y < colorPalette.height; y++) {
      for (let x = 0; x < colorPalette.width; x++) {
        const s = x / colorPalette.width;
        const v = 1 - y / colorPalette.height;
        const { red, green, blue } = hsvToRgbDevided(h, s, v);

        this.imageData.data[i + 0] = red;
        this.imageData.data[i + 1] = green;
        this.imageData.data[i + 2] = blue;
        this.imageData.data[i + 3] = 255;
        i += 4;
      }
    }
    colorPaletteCtx.putImageData(this.imageData, 0, 0);
  }

  colorPaletteSelect(e: MouseEvent) {
    const { colorPalette } = this;
    if (!colorPalette) return;
    this.getMouseCoords(e, colorPalette);
    this.selectColor();
  }

  createColorSlider() {
    const { colorSlider, colorSliderCtx } = this;
    if (!colorSlider || !colorSliderCtx) return;
    const hueGradient = colorSliderCtx.createLinearGradient(
      0,
      colorSlider.height,
      colorSlider.width,
      colorSlider.height
    );
    hueGradient.addColorStop(0.0, '#ff0000');
    hueGradient.addColorStop(0.17, '#ffff00');
    hueGradient.addColorStop(0.33, '#00ff00');
    hueGradient.addColorStop(0.5, '#00ffff');
    hueGradient.addColorStop(0.67, '#0000ff');
    hueGradient.addColorStop(0.83, '#ff00ff');
    hueGradient.addColorStop(1.0, '#ff0000');

    colorSliderCtx.fillStyle = hueGradient;
    colorSliderCtx.fillRect(0, 0, colorSlider.width, colorSlider.height);
  }

  onCPSMouseDown(e: MouseEvent) {
    this.colorPalletterClicked = true;
    this.isSelectorMoved = true;
    this.colorPaletteSelect(e);
  }

  onCPSMouseUp(e: MouseEvent) {
    this.colorSliderClicked = false;
    this.colorPalletterClicked = false;
    this.isSelectorMoved = false;
    this.colorPaletteSelect(e);
  }

  onCPMouseDown(e: MouseEvent) {
    this.colorPalletterClicked = true;
    this.colorPaletteSelect(e);
  }

  onSSMouseDown(e: MouseEvent) {
    const { colorSlider, colorSliderCtx } = this;
    if (!colorSlider || !colorSliderCtx) return;
    this.colorSliderClicked = true;
    this.colorSliderSelect(e, colorSlider, colorSliderCtx);
  }

  onSSMouseUp() {
    this.colorSliderClicked = false;
    this.colorPalletterClicked = false;
    this.isSelectorMoved = false;
  }

  onCSMouseDown(e: MouseEvent) {
    const { colorSlider, colorSliderCtx } = this;
    if (!colorSlider || !colorSliderCtx) return;

    this.colorSliderClicked = true;
    this.colorSliderSelect(e, colorSlider, colorSliderCtx);
  }

  onMouseMove(e: MouseEvent) {
    const { colorSlider, colorSliderCtx, colorSliderClicked, colorPalletterClicked } = this;
    if (e.buttons !== 1) return;
    if (colorSliderClicked) {
      if (!colorSlider || !colorSliderCtx) return;
      this.colorSliderSelect(e, colorSlider, colorSliderCtx);
    }
    if (colorPalletterClicked) {
      this.colorPaletteSelect(e);
    }
  }

  onCPMouseMove(e: MouseEvent) {
    e.stopPropagation();
    const { colorPalletterClicked } = this;
    if (colorPalletterClicked) {
      this.colorPaletteSelect(e);
    }
    this.colorSliderClicked = false;
  }

  onCSMouseMove(e: MouseEvent) {
    e.stopPropagation();
    const { colorSliderClicked } = this;
    if (colorSliderClicked) {
      this.colorPaletteSelect(e);
    }
    this.colorPalletterClicked = false;
  }

  onMouseUp() {
    this.colorSliderClicked = false;
    this.colorPalletterClicked = false;
    this.onColorChange();
  }

  colorSliderSelect(e: MouseEvent, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    const { x } = this.colorCoords;
    const mouse = this.getMouseCoords(e, canvas);
    const [r, g, b] = ctx.getImageData(mouse.x, 0, 1, 1).data;
    const { h } = rgbToHsv(r, g, b);
    this.createColorPalette(h);
    const { sliderSelector } = this;
    const selectedColor = `rgb(${r},${g},${b})`;

    if (sliderSelector) {
      sliderSelector.style.background = selectedColor;
      sliderSelector.style.left = `${mouse.x}px`;
    }
    if (!this.isSelectorMoved) {
      const selector = this.shadowRoot?.querySelector<HTMLElement>('.selector');
      if (selector) {
        selector.style.background = selectedColor;
      }
      this.startColor = selectedColor;
      if (x === this?.colorPalette?.width) {
        return;
      }
    }
    this.selectColor();
  }

  onColorChange() {
    const selector = this.shadowRoot?.querySelector<HTMLElement>('.selector');
    if (selector) {
      const selectedColor = toHexColor(selector.style.background);
      // TODO: Need to add custom event in documentation
      this.sendCustomEvent('teColorChange', selectedColor);
    }
  }

  getColorPickerModalStyle() {
    return {
      visibility: this.isCustom ? (this.isColorPickerOpen ? 'visible' : 'hidden') : 'visible',
      flexDirection: this.variant === 'hybrid' ? 'row' : 'unset',
      paddingRight: this.variant === 'hybrid' ? '72px' : '16px',
    };
  }

  getColorStyle(color: string, key?: number, isNew?: boolean) {
    let selectedColor = color;

    if (isNew && this.isNewColorSelected && key === this.selectedColorIndex) {
      selectedColor = this.startColor;
    }
    selectedColor = toHexColor(selectedColor);
    return {
      background: selectedColor ? selectedColor : `${theme.colors.shade2}`,
      border: selectedColor === '#FFFFFF' ? `1px solid ${theme.colors.shade2}` : 'none',
      boxShadow:
        selectedColor.toUpperCase() === this.startColor.toUpperCase()
          ? `0px 0px 0px 2px ${theme.colors.selectedColor}`
          : 'unset',
    };
  }

  getCurrentColorStyle() {
    return {
      boxShadow: this.isColorPickerOpen ? `0px 0px 0px 2px ${theme.colors.selectedColor}` : 'unset',
      background: this.startColor,
      bottom: this.variant === 'predefined' ? '80px !important' : '4px',
    };
  }

  onNewColorAdd() {
    const { startColor, newColors } = this;

    if (startColor.includes('rgb')) {
      const { red, green, blue } = getRGB(this.startColor);
      if (!red || !green || !blue) return;
      this.startColor = rgbToHex(red, green, blue);
    }
    const found = [...predefinedColorsList, ...newColors].some(
      (color) => color.toUpperCase() === startColor.toUpperCase()
    );
    if (found || newColors.length === 4) return;
    this.newColors = [...newColors, startColor];
  }

  onNewColorSelect(e: Event, key: number, color: string) {
    e.preventDefault();
    e.stopPropagation();
    this.isNewColorSelected = key === this.selectedColorIndex ? !this.isNewColorSelected : true;
    this.startColor = color;
    this.createColorPicker();
    this.selectedColorIndex = key;
  }

  keyDownFunction(event: KeyboardEvent) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      // key codes for backspace and delete
      const updatedColorsList = this.newColors.filter(
        (c) => c.toUpperCase() !== this.startColor.toUpperCase()
      );
      this.newColors = updatedColorsList;
      this.selectedColorIndex = undefined;
      this.isNewColorSelected = false;
      this.startColor = DEFAULT_COLOR;
      this.createColorPicker();
    }
  }

  onDefinedColorSelect(e: Event, color: string) {
    e.preventDefault();
    e.stopPropagation();
    this.startColor = color;
    if (this.variant === 'hybrid') this.createColorPicker();
    this.sendCustomEvent('teColorChange', color);
    this.isNewColorSelected = false;
  }

  hsvToRgb(h: number, s: number, v: number) {
    const { red, green, blue } = hsvToRgbDevided(h, s, v);
    return `rgb(${red},${green},${blue})`;
  }

  checkForOtherColorPickers() {
    const elementsArray = Array.from(document.getElementsByTagName('te-color-picker'));
    elementsArray.forEach((ele) => {
      if (ele !== this && ele.isColorPickerOpen) ele.isColorPickerOpen = false;
    });
  }

  render() {
    return html`
      <div>
        <div class="te-color-picker" style=${styleMap(this.getColorPickerModalStyle())}>
          ${this.variant === 'predefined'
            ? html`
                <div class="color-list">
                  ${predefinedColorsList.map(
                    (color) =>
                      html`
                        <div
                          class="color-list-color"
                          style=${styleMap(this.getColorStyle(color))}
                          @click=${(e: Event) => this.onDefinedColorSelect(e, color)}
                        ></div>
                      `
                  )}
                </div>
              `
            : html`
                <div class="normal">
                  <div
                    class="color-palette"
                    @mousemove=${this.onMouseMove}
                    @mouseup=${this.onMouseUp}
                  >
                    <canvas width="160" height="160" @mousedown=${this.onCPMouseDown}></canvas>
                    <div
                      class="selector"
                      id="canvas-selector"
                      @mousedown=${this.onCPSMouseDown}
                    ></div>
                  </div>
                  <div
                    class="color-slider"
                    @mousemove=${this.onMouseMove}
                    @mouseup=${this.onMouseUp}
                  >
                    <canvas width="160" height="8" @mousedown=${this.onCSMouseDown}></canvas>
                    <div
                      class="selector"
                      id="slider-selector"
                      @mousedown=${this.onSSMouseDown}
                      @mouseup=${this.onSSMouseUp}
                    ></div>
                  </div>
                </div>
                ${this.variant === 'hybrid'
                  ? html`
                      <div class="color-list-container">
                        <div class="color-list-hybrid">
                          ${predefinedColorsList.map(
                            (color) =>
                              html`
                                <div
                                  class="color-list-color"
                                  style=${styleMap(this.getColorStyle(color))}
                                  @click=${(e: Event) => this.onDefinedColorSelect(e, color)}
                                ></div>
                              `
                          )}
                        </div>
                        <div class="color-list-hybrid-new">
                          ${this.newColors.map(
                            (color, key) => html`
                              <div
                                tabindex="-1"
                                @keydown=${this.keyDownFunction}
                                class="color-list-color"
                                style=${styleMap(this.getColorStyle(color, key, true))}
                                @click=${(e: Event) => this.onNewColorSelect(e, key, color)}
                              ></div>
                            `
                          )}
                          ${this.newColors.length < 4
                            ? html`
                                <te-icon
                                  name="plus"
                                  size="8"
                                  @click=${this.onNewColorAdd}
                                ></te-icon>
                              `
                            : null}
                        </div>
                      </div>
                    `
                  : null}
              `}
        </div>

        <div
          @click=${(e: Event) => {
            e.stopPropagation();
            e.preventDefault();
            if (this.isCustom) this.checkForOtherColorPickers();
            this.isColorPickerOpen = !this.isColorPickerOpen;
          }}
        >
          <slot></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-color-picker': TeColorPicker;
  }
}
